import enviro from 'enviro';
import { useEffect } from 'react';
import { useExperimentContext } from '../../context/ExperimentContext';
import { getIsAcceptanceTest } from '../envUtils';
import { logExposure } from '../laboratoryClient';
import { EXPERIMENT_GROUPS } from './experimentTypes';
const alwaysEligible = () => ({
  isConditionMet: true
});

// Dedupes `laboratoryClient.logExposure` to once per treatment per SPA session.
// Laboratory exposure semantics are "user was observed in this cohort during this
// session", not per route view, so SPA navigation between eligible routes should
// not re-log. State only resets on full page reload.
const loggedExposureKeys = new Set();

/**
 * Test-only helper to reset the deduplication state between specs.
 */
export const resetLoggedExposureKeys = () => {
  loggedExposureKeys.clear();
};

/**
 * Log experiment exposure
 */
export const logExperimentExposure = treatmentKey => {
  if (loggedExposureKeys.has(treatmentKey)) {
    return;
  }
  loggedExposureKeys.add(treatmentKey);
  logExposure(treatmentKey);
};

/**
 * Read a forced experiment group from the URL. Format:
 *   ?forceExperiment=<treatmentKey>:<group>[,<treatmentKey>:<group>...]
 *
 * Restricted to QA so prod traffic always lands in a Laboratory-assigned cohort.
 */
export const getForcedExperimentGroup = treatmentKey => {
  if (typeof window === 'undefined' || !enviro.isQa()) {
    return undefined;
  }
  const raw = new URLSearchParams(window.location.search).get('forceExperiment');
  if (!raw) {
    return undefined;
  }
  for (const entry of raw.split(',')) {
    const [key, group] = entry.split(':');
    if (key === treatmentKey && group) {
      return group;
    }
  }
  return undefined;
};
export function isGroupOption(groupParameterOptions, value) {
  return Object.values(groupParameterOptions).some(option => option !== undefined && option === value);
}

/**
 * Get experiment data
 *
 * @param treatmentKey - The treatment key for the experiment
 * @param groupParameterKey - The group parameter key for the experiment, defaults to 'group'
 * @param groupParameterOptions - The group parameter options for the experiment
 * @param experimentEligibilityCondition - The eligibility condition for the experiment, defaults to a condition that is always met
 */
export const useExperiment = ({
  treatmentKey,
  groupParameterKey = 'group',
  groupParameterOptions,
  experimentEligibilityCondition = alwaysEligible
}) => {
  var _treatments$treatment;
  const {
    treatments,
    isLoading,
    hasFetched
  } = useExperimentContext();
  const forcedGroup = getForcedExperimentGroup(treatmentKey);
  const resolvedGroup = forcedGroup !== null && forcedGroup !== void 0 ? forcedGroup : (_treatments$treatment = treatments[treatmentKey]) === null || _treatments$treatment === void 0 ? void 0 : _treatments$treatment.parameters[groupParameterKey];
  const isAcceptanceTest = getIsAcceptanceTest();
  const {
    isConditionMet
  } = experimentEligibilityCondition();
  const isEligible = hasFetched && (!isAcceptanceTest || !!forcedGroup) && isConditionMet && isGroupOption(groupParameterOptions, resolvedGroup);
  const group = isEligible ? resolvedGroup : undefined;
  return {
    treatmentKey,
    isEligible,
    isFetched: hasFetched,
    isLoading,
    group
  };
};
const isExperimentGroup = value => EXPERIMENT_GROUPS.some(group => group === value);
const toRegisteredGroup = group => isExperimentGroup(group) ? group : 'control';

/**
 * Use this hook to log exposure, update eligibility state, and return experiment data
 *
 * @param treatmentKey - The treatment key for the experiment
 * @param experimentEligibilityCondition - The eligibility condition for the experiment, defaults to a condition that is always met
 * @param groupParameterOptions - The group parameter options for the experiment
 * @param groupParameterKey - The group parameter key for the experiment, defaults to 'group'
 * @param shouldLogExposure - Whether to log exposure for the experiment, defaults to true
 */
export const useExperimentEffect = ({
  treatmentKey,
  experimentEligibilityCondition = alwaysEligible,
  groupParameterOptions,
  groupParameterKey = 'group',
  shouldLogExposure = true
}) => {
  const {
    registerEligibility
  } = useExperimentContext();
  const experiment = useExperiment({
    treatmentKey,
    experimentEligibilityCondition,
    groupParameterOptions,
    groupParameterKey
  });
  const {
    isEligible,
    group
  } = experiment;
  const isForced = getForcedExperimentGroup(treatmentKey) !== undefined;

  // No cleanup on unmount: cohort eligibility is sticky for the SPA session
  // (intent-to-treat). A user exposed on one page must keep the cohort tag
  // when navigating to and converting from another, otherwise downstream
  // attribution breaks. Whether the variant *renders* on a given page is a
  // separate component-level concern. See arbor docs/experiments.mdx.
  useEffect(() => {
    if (isEligible && shouldLogExposure && !isForced) {
      logExperimentExposure(treatmentKey);
    }
    registerEligibility({
      treatmentKey,
      isEligible,
      group: toRegisteredGroup(group),
      isPublic: true,
      isInApp: false
    });
  }, [registerEligibility, isEligible, treatmentKey, group, shouldLogExposure, isForced]);
  return experiment;
};